import { chain, every, isEmpty, isFunction, isNil, merge, set } from 'lodash';

import { IModalDialogOptions } from '@app/shared/types/ui/modal-options.type';
import {
  defaultWidgetData,
  gridSelectModalOptions,
} from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.settings';
import { WIDGET_COMPONENT } from '@app/shared/types/enums/widget-component.enum';
import { stringCaseInsensitiveComparator } from '@app/shared/common/string-case-insensitive.comparator/string-case-insensitive.comparator';
import { CELL_RENDERER } from '@app/shared/types/enums/cell-renderer.enum';
import { PERCENTAGE_SIGN, POSITION_RIGHT } from '@config/global-constants';
import {
  BTN_CANCEL,
  BTN_CONTINUE,
} from '@app/text-content/button-templates/button-templates-content';

import {
  IReplenishConfirmBuilderParams,
  ReplenishConfirmBuilder,
} from './replenish-confirm.types';
import { REPLENISH_APPLY_ITEMS_SCOPE } from '../replenish.enums';
import { getReplenishItemOptions } from './builder.utils';
import { IReplenishDestination } from '../replenish.types';
import { DEFAULT_REPLENISH_PERCENT } from '../replenish.constants';
import { I18n } from '@app/i18n/strings-keys';

const bulkReplenishModalBuilder: ReplenishConfirmBuilder = (
  params,
): IModalDialogOptions => {
  const percentCellRendererDefaultParams = {
    defaultValue: DEFAULT_REPLENISH_PERCENT,
    symbolPosition: POSITION_RIGHT,
    precision: 0,
    label: PERCENTAGE_SIGN,
    max: 999,
    min: 1,
    usePreferenceDecimalPrecision: {
      overrideMin: false,
      overrideMax: false,
    },
    disableKeyUpFinishEditing: true,
    allowEmptyValue: true,
    forceConversionOfZeroToEmpty: true,
  };

  const defaultParams: Partial<IReplenishConfirmBuilderParams> = {
    showItemOptions: true,
  };

  const modalParams = merge({}, defaultParams, params);

  const replenishSites = modalParams.replenishSite as IReplenishDestination[];

  const modalConfig = merge(
    {
      schema: {
        properties: {
          itemOption: { type: 'string', required: modalParams.showItemOptions },
          items: { type: 'array', required: true },
        },
      },
    },
    gridSelectModalOptions(undefined, params.i18nService),
    defaultWidgetData,
    <Partial<IModalDialogOptions>>{
      title: isEmpty(modalParams.modalTitle)
        ? I18n.Forms.TransferForm.BulkReplenishToPAR
        : modalParams.modalTitle,
      modalClass: `modal-lg`,
      modalResultMapper: isFunction(modalParams.modalResultMapper)
        ? modalParams.modalResultMapper
        : (value: { items: any }) => {
            const destinationWithPercent = chain(value.items)
              .reduce(
                (dict, curItem: IReplenishDestination) => ({
                  ...dict,
                  [curItem.toSite]: {
                    ...curItem,
                    fromSite: curItem.fromSite,
                    toSite: curItem.toSite,
                    percent: curItem.percent,
                    skipReplenish: isNil(curItem.percent),
                  },
                }),
                {},
              )
              .value();

            return {
              ...value,
              items: destinationWithPercent,
            };
          },
      formRules: {
        fields: {
          itemOption: getReplenishItemOptions({
            showItemOptions: modalParams.showItemOptions,
            itemOptionsClass: 'select-full-width',
          }),
          items: {
            title: '',
            widget: WIDGET_COMPONENT.edit_grid,
            widgetData: {
              showSearch: true,
              multiselect: false,
              orderControls: false,
              disableColumnSelector: true,
              showPinnedRow: true,
              valueField: 'toSite',
              ignoreItemValueOptimization: true,
              pinnedTopRowData: [
                {
                  changes: null,
                  current: null,
                  schedule_info: [],
                  site: { toSite: 'pinned' },
                },
              ],
            },
          },
        },
      },
      buttons: [
        {
          ...BTN_CONTINUE,
          disabled: (formValue): boolean =>
            every(formValue.items, (item) => isNil(item.percent)),
        },
        BTN_CANCEL,
      ],
    },
  );

  // please use set instead of merge when your new property levels deep
  set(
    modalConfig,
    'formRules.fields.items.widgetData.selectOptions',
    replenishSites,
  );
  set(modalConfig, 'formRules.fields.items.widgetData.columns', [
    {
      title: I18n.Sites.SiteId,
      path: 'toSiteStoreNumber',
      defaultView: true,
      sortable: true,
      comparator: stringCaseInsensitiveComparator,
      pinnedRowCellRenderer: CELL_RENDERER.count_renderer,
      pinnedRowCellRendererParams: {
        label: params.i18nService.instant(I18n.Sites.AllSites),
      },
      filterParams: {
        quickFilter: true,
      },
    },
    {
      title: I18n.Sites.SiteName,
      path: 'toSiteName',
      defaultView: true,
      comparator: stringCaseInsensitiveComparator,
      sortable: true,
      filterParams: {
        quickFilter: true,
      },
    },
    {
      title: I18n.Forms.ReplenishForm.ReplenishToPercentOfPAR,
      path: 'percent',
      defaultView: true,
      cellRenderer: CELL_RENDERER.price_input_renderer,
      cellRendererParams: percentCellRendererDefaultParams,
      pinnedRowCellRenderer: CELL_RENDERER.price_input_renderer,
      pinnedRowCellRendererParams: merge({}, percentCellRendererDefaultParams, {
        placeholder: I18n.Forms.ReplenishForm.SetAll,
      }),
    },
  ]);
  set(modalConfig, 'value', {
    itemOption: REPLENISH_APPLY_ITEMS_SCOPE.CHARGEABLE_ONLY,
    items: replenishSites,
  });

  return modalConfig;
};

export { bulkReplenishModalBuilder };
